import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import { AuthService } from './auth.service';
import { downloadGet, downloadPost } from '../utils/downloadFile';
import {
  AssetCount, AssetCountLine, CountResult, LabelLayout, LabelRequest, LineUpdateRequest, ScanCountRequest, ScanResolution,
  StartCountRequest,
} from '../interfaces/fixedAsset/fixedAssetCount.interface';

const E = ENDPOINTS.FIXED_ASSET_COUNT;
const institution = () => AuthService.getSelectedInstitution()!;

export const FixedAssetCountService = {
  list: async (): Promise<AssetCount[]> =>
    (await axios.get(E.BASE, { params: { institutionId: institution() } })).data,
  get: async (id: string): Promise<AssetCount> => (await axios.get(E.GET(id))).data,
  start: async (dto: Omit<StartCountRequest, 'institutionId'>): Promise<AssetCount> =>
    (await axios.post(E.BASE, { ...dto, institutionId: institution() })).data,
  lines: async (id: string, result?: CountResult | '', q?: string): Promise<AssetCountLine[]> =>
    (await axios.get(E.LINES(id), { params: { result: result || undefined, q: q?.trim() || undefined } })).data,
  scan: async (id: string, dto: ScanCountRequest): Promise<AssetCountLine> =>
    (await axios.post(E.SCAN(id), dto)).data,
  updateLine: async (id: string, lineId: string, dto: LineUpdateRequest): Promise<AssetCountLine> =>
    (await axios.put(E.LINE(id, lineId), dto)).data,
  close: async (id: string): Promise<AssetCount> => (await axios.post(E.CLOSE(id))).data,
  cancel: async (id: string): Promise<AssetCount> => (await axios.post(E.CANCEL(id))).data,
  exportCsv: (id: string, result?: CountResult | '', q?: string): Promise<void> =>
    downloadGet(E.EXPORT_CSV(id), { result: result || undefined, q: q?.trim() || undefined }, 'asset-count.csv'),
  exportPdf: (id: string, result?: CountResult | '', q?: string): Promise<void> =>
    downloadGet(E.EXPORT_PDF(id), { result: result || undefined, q: q?.trim() || undefined }, 'asset-count.pdf'),
};

export const FixedAssetLabelService = {
  /** Downloads the sticker PDF. {@code origin} lets a staging site print staging QR codes (the server checks it). */
  print: (req: Omit<LabelRequest, 'institutionId' | 'origin'>): Promise<void> =>
    downloadPost(ENDPOINTS.FIXED_ASSET_LABELS,
      { ...req, institutionId: institution(), origin: window.location.origin }, 'asset-labels.pdf'),
};

export const EquipmentLabelService = {
  /** Machine QR stickers (manage key). Same sheet layouts as the asset labels. */
  print: (req: { equipmentIds: string[]; layout: LabelLayout; startPosition?: number }): Promise<void> =>
    downloadPost(ENDPOINTS.MAINTENANCE_EQUIPMENT_LABELS,
      { ...req, institutionId: institution(), origin: window.location.origin }, 'equipment-labels.pdf'),
};

export interface ReceiptVerification {
  receiptNumber: string;
  amount: number;
  currency: string;
  receiptDate: string;
  companyName: string;
  status: 'valid' | 'reversed';
}

/** A bare client: no staff bearer token and no logout-on-401 interceptor, so a stale login cannot disturb a public page. */
const publicHttp = () => {
  const http = axios.create();
  delete http.defaults.headers.common['Authorization'];
  return http;
};

/** Unauthenticated: the signed token in the QR link is the proof. */
export const ReceiptVerifyService = {
  get: async (id: string, token: string): Promise<ReceiptVerification> =>
    (await publicHttp().get(ENDPOINTS.PUBLIC_RECEIPT_VERIFY(id, token))).data,
};

export const ScanService = {
  resolve: async (type: string, id: string): Promise<ScanResolution> =>
    (await axios.get(ENDPOINTS.SCAN.RESOLVE, { params: { type, id } })).data,
};
